import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, ArrowLeft,
  Star, Trophy, Flame, TrendingUp, Clock,
  Calculator, BookOpen, FlaskConical, Target
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const StudentDetail = () => {
  const { studentId } = useParams();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const sidebarItems = [
    { path: '/tutor', label: 'Dashboard', icon: LayoutDashboard },
  ];

  useEffect(() => {
    const fetchStudentDetails = async () => {
      try {
        const response = await api.get(`/v2/tutor/student/${studentId}`);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching student details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStudentDetails();
  }, [api, studentId]);

  const subjectInfo = {
    maths: { icon: Calculator, color: 'amber', name: 'Mathematics' },
    english: { icon: BookOpen, color: 'pink', name: 'English' },
    science: { icon: FlaskConical, color: 'emerald', name: 'Science' },
    esol: { icon: BookOpen, color: 'blue', name: 'ESOL' },
    '11plus': { icon: Target, color: 'purple', name: '11+ Preparation' },
  };

  const keyStageLabels = {
    ks1: 'Key Stage 1',
    ks2: 'Key Stage 2',
    ks3: 'Key Stage 3',
    ks4: 'Key Stage 4',
    esol: 'ESOL',
    '11plus': '11+ Preparation'
  };

  if (loading) {
    return (
      <ProLayout title="Student Details" sidebarItems={sidebarItems}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
        </div>
      </ProLayout>
    );
  }

  if (!data) {
    return (
      <ProLayout title="Student Details" sidebarItems={sidebarItems}>
        <div className="text-center py-12">
          <p className="text-slate-500">Could not load student data</p>
          <Link to="/tutor">
            <button className="mt-4 text-purple-600 font-semibold">
              <ArrowLeft className="w-4 h-4 inline mr-2" />
              Back to Dashboard
            </button>
          </Link>
        </div>
      </ProLayout>
    );
  }

  const { student, progress, stats } = data;

  // Group progress by subject
  const progressBySubject = progress.reduce((acc, p) => {
    const subj = p.subject || 'unknown';
    if (!acc[subj]) {
      acc[subj] = { lessons: 0, totalScore: 0, totalQuestions: 0, timeMinutes: 0 };
    }
    acc[subj].lessons += 1;
    acc[subj].totalScore += p.score || 0;
    acc[subj].totalQuestions += p.total_questions || 0;
    acc[subj].timeMinutes += Math.round((p.time_spent_seconds || 0) / 60);
    return acc;
  }, {});

  return (
    <ProLayout title={`${student.first_name}'s Progress`} sidebarItems={sidebarItems}>
      <div data-testid="student-detail">
        {/* Back Button */}
        <Link to="/tutor" className="inline-flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Students
        </Link>

        {/* Student Header */}
        <div className="pro-card bg-gradient-to-r from-purple-500 to-indigo-600 text-white mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <span className="font-bold text-white text-2xl">
                  {student.first_name?.charAt(0)}
                </span>
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">
                  {student.first_name} {student.last_name}
                </h1>
                <p className="text-purple-100">
                  Year {student.year_group} • {keyStageLabels[student.key_stage]}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-5 h-5 text-orange-300" />
                  <span className="text-2xl font-bold">{student.streak_days || 0}</span>
                </div>
                <p className="text-xs text-purple-100">Day Streak</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="w-5 h-5 text-yellow-300" />
                  <span className="text-2xl font-bold">{student.total_points || 0}</span>
                </div>
                <p className="text-xs text-purple-100">Total Points</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="pro-card text-center">
            <TrendingUp className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{stats.lessons_completed}</p>
            <p className="text-sm text-slate-500">Lessons Completed</p>
          </div>
          <div className="pro-card text-center">
            <Star className="w-8 h-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{Math.round(stats.average_score)}%</p>
            <p className="text-sm text-slate-500">Average Score</p>
          </div>
          <div className="pro-card text-center">
            <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{stats.total_time_minutes}</p>
            <p className="text-sm text-slate-500">Minutes Spent</p>
          </div>
          <div className="pro-card text-center">
            <Trophy className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{student.total_stars || 0}</p>
            <p className="text-sm text-slate-500">Stars Earned</p>
          </div>
        </div>

        {/* Progress by Subject */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Progress by Subject</h2>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {Object.entries(progressBySubject).length === 0 ? (
            <div className="col-span-3 pro-card text-center py-8">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No lessons completed yet</p>
            </div>
          ) : (
            Object.entries(progressBySubject).map(([subject, subjectStats]) => {
              const info = subjectInfo[subject] || { icon: BookOpen, color: 'gray', name: subject };
              const avgScore = subjectStats.totalQuestions > 0 
                ? Math.round((subjectStats.totalScore / subjectStats.totalQuestions) * 100)
                : 0;
              
              return (
                <div key={subject} className="pro-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 bg-${info.color}-100 rounded-lg flex items-center justify-center`}>
                      <info.icon className={`w-6 h-6 text-${info.color}-600`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 capitalize">{info.name}</h3>
                      <p className="text-sm text-slate-500">{subjectStats.lessons} lessons</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Average Score</span>
                        <span className="font-semibold text-slate-800">{avgScore}%</span>
                      </div>
                      <Progress value={avgScore} className="h-2" />
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Time Spent</span>
                      <span className="font-semibold text-slate-800">{subjectStats.timeMinutes} mins</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Recent Activity */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Recent Activity</h2>
        <div className="pro-card">
          {progress.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No recent activity</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Lesson</th>
                    <th>Subject</th>
                    <th>Score</th>
                    <th>Stars</th>
                    <th>Time</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {progress.slice(0, 10).map((p, idx) => (
                    <tr key={idx}>
                      <td className="font-medium text-slate-700">{p.lesson_id}</td>
                      <td>
                        <span className="capitalize px-2 py-0.5 bg-slate-100 rounded text-sm">
                          {p.subject}
                        </span>
                      </td>
                      <td>
                        <span className={`font-semibold ${p.percentage >= 70 ? 'text-emerald-600' : p.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {Math.round(p.percentage)}%
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {[...Array(p.stars_earned || 0)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                          ))}
                        </div>
                      </td>
                      <td className="text-slate-600">{Math.round(p.time_spent_seconds / 60)} min</td>
                      <td className="text-slate-500 text-sm">
                        {new Date(p.completed_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default StudentDetail;
